import { type SxProps, type Theme } from '@mui/material';

import { colors } from 'common';

const CODE_MIRROR_BORDER_RADIUS = '4px';
// CodeMirror has a very specific selector, so we need to match or beat it.
const CODE_MIRROR_UL_SELECTOR = '.cm-tooltip.cm-tooltip-autocomplete ul';

interface CodeMirrorFormFieldProps {
  isEditable: boolean;
  editorElementStyles?: Record<string, string | number>;
}

export function getCodeMirrorFormFieldSxProps(props: CodeMirrorFormFieldProps): SxProps<Theme> {
  const { isEditable, editorElementStyles } = props ?? {};
  return {
    '& .cm-tooltip': {
      // This ensures that autocomplete is above other elements
      zIndex: 1,
      backgroundColor: (theme) => theme.palette.background.paper,
      fontSize: (theme) => theme.typography.body1.fontSize,
    },
    '& .cm-placeholder': {
      color: colors.extended.gray[45],
    },
    [`& ${CODE_MIRROR_UL_SELECTOR} li`]: {
      lineHeight: 2,
    },
    [`& ${CODE_MIRROR_UL_SELECTOR} li[aria-selected]`]: {
      color: (theme) => theme.typography.body1.color,
      backgroundColor: 'rgba(11,103,253,0.08)',
    },
    [`& ${CODE_MIRROR_UL_SELECTOR} li:hover`]: {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
    '& .cm-editor': {
      borderRadius: CODE_MIRROR_BORDER_RADIUS,
      fontSize: (theme) => theme.typography.body1.fontSize,
      ...editorElementStyles,
      /**
       * Code mirrors use colors to provide hints about content, including errors.
       * In a non-editable state, we still want to highlight hints, but make it more clear that
       * the field is read-only.
       */
      cursor: isEditable ? undefined : 'not-allowed',
    },
    '& .cm-editor.cm-focused': {
      outline: 'none',
    },
  };
}

export const defaultCodeMirrorProps = {
  indentWithTab: false,
};

export type CodeMirrorVariant = 'standard' | 'borderless';

const DEFAULT_CODE_MIRROR_STYLES = {
  border: '1px solid',
  borderColor: colors.border.default,
  borderRadius: CODE_MIRROR_BORDER_RADIUS,
  fontSize: '0.875rem',
};

const ERROR_CODE_MIRROR_STYLES = {
  ...DEFAULT_CODE_MIRROR_STYLES,
  borderColor: colors.error.default,
};

interface CodeMirrorStyleOptions {
  isFieldErrorVisible: boolean;
  variant?: CodeMirrorVariant;
}
export function getCodeMirrorStyles(options: CodeMirrorStyleOptions) {
  const { isFieldErrorVisible, variant } = options;
  return {
    ...DEFAULT_CODE_MIRROR_STYLES,
    ...(isFieldErrorVisible && ERROR_CODE_MIRROR_STYLES),
    ...(variant === 'borderless' && { border: 'none' }),
  };
}
